import { useState } from "react";
import { Link2, Loader2, Search, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

// Spam score is 0–100; lower is better. Color it so a glance tells quality.
function spamColor(score) {
  if (score == null) return "#9CA3AF";
  if (score <= 10) return "#00C853";
  if (score <= 30) return "#FFD600";
  return "#FF3B30";
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-4 flex flex-col justify-center">
      <span className="uppercase-label">{label}</span>
      <span className="font-heading text-3xl font-black tracking-tighter mt-1" style={color ? { color } : {}}>
        {value ?? "—"}
      </span>
    </div>
  );
}

export default function Backlinks({ reportId, initialData }) {
  const [summary, setSummary] = useState(initialData || null);
  const [loading, setLoading] = useState(false);

  const [competitor, setCompetitor] = useState("");
  const [gap, setGap] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);

  const runSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${reportId}/backlinks`);
      setSummary(data);
    } catch (e) {
      toast.error("Could not load backlink profile", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const runGap = async () => {
    const domain = competitor.trim();
    if (!domain) {
      toast.error("Enter a competitor domain first");
      return;
    }
    setGapLoading(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/backlinks/gap`, { competitor_domain: domain });
      setGap(data);
      if (!data.gaps?.length) {
        toast.info("No link gap found", { description: "No qualifying domains link to them but not you." });
      }
    } catch (e) {
      toast.error("Could not run gap analysis", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setGapLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fade-up" data-testid="backlinks">
      <div className="flex items-center gap-2 mb-5">
        <Link2 className="w-5 h-5 text-klein" />
        <h3 className="font-heading text-xl font-bold tracking-tight">Backlink Profile</h3>
      </div>

      {!summary ? (
        <div className="text-center py-8">
          <p className="text-gray-500 max-w-md mx-auto text-sm">
            Pull this domain's authority, total backlinks, referring domains and spam signals — then
            compare against a competitor to find link opportunities.
          </p>
          <Button onClick={runSummary} disabled={loading} className="mt-5 bg-klein hover:bg-blue-800 text-white" data-testid="backlinks-run">
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
            ) : (
              "Analyze backlinks"
            )}
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Authority Rank" value={summary.rank} color="#002FA7" />
            <StatCard label="Backlinks" value={summary.backlinks?.toLocaleString?.() ?? summary.backlinks} />
            <StatCard label="Referring Domains" value={summary.referring_domains?.toLocaleString?.() ?? summary.referring_domains} />
            <StatCard label="Spam Score" value={summary.spam_score != null ? `${summary.spam_score}%` : "—"} color={spamColor(summary.spam_score)} />
            <StatCard label="Broken Links" value={summary.broken_backlinks} color={summary.broken_backlinks > 0 ? "#FF3B30" : undefined} />
          </div>

          {/* Backlink gap analysis */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <h4 className="font-heading text-lg font-bold tracking-tight">Link Gap Analysis</h4>
            <p className="text-sm text-gray-500 mt-1">
              Find domains that link to a competitor but not to you — filtered by quality, ranked by authority.
            </p>
            <div className="flex items-center gap-2 mt-4 max-w-md">
              <Input
                placeholder="competitor.com"
                value={competitor}
                onChange={(e) => setCompetitor(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runGap()}
                data-testid="gap-competitor-input"
              />
              <Button onClick={runGap} disabled={gapLoading} className="bg-[#0A0A0A] hover:bg-gray-800 flex-shrink-0" data-testid="gap-run">
                {gapLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {gap && gap.gaps?.length > 0 && (
              <div className="mt-5 border border-gray-200 rounded-xl overflow-hidden" data-testid="gap-results">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-xs text-gray-500">
                  Showing {gap.gaps.length} opportunities linking to{" "}
                  <span className="font-semibold text-gray-700">{gap.competitor_domain}</span>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-4">Domain</TableHead>
                        <TableHead className="text-right">Rank</TableHead>
                        <TableHead className="text-right">Backlinks</TableHead>
                        <TableHead className="text-right">Ref. Domains</TableHead>
                        <TableHead className="text-right pr-4">Spam</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gap.gaps.map((g, i) => (
                        <TableRow key={i} data-testid={`gap-row-${i}`}>
                          <TableCell className="pl-4 font-medium">
                            <a href={`https://${g.domain}`} target="_blank" rel="noreferrer" className="text-klein hover:underline">
                              {g.domain}
                            </a>
                          </TableCell>
                          <TableCell className="text-right">{g.rank}</TableCell>
                          <TableCell className="text-right">{g.backlinks?.toLocaleString?.() ?? g.backlinks}</TableCell>
                          <TableCell className="text-right">{g.referring_domains?.toLocaleString?.() ?? g.referring_domains}</TableCell>
                          <TableCell className="text-right pr-4">
                            <span className="inline-flex items-center gap-1 font-semibold" style={{ color: spamColor(g.spam_score) }}>
                              {g.spam_score > 30 && <ShieldAlert className="w-3.5 h-3.5" />}
                              {g.spam_score}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
